/**
 * Lightweight JWT payload decoder. Signature validation is the resource
 * servers' job (tmm-api, saas-auth) — this is a read-only client-side parse used
 * only to gate the shell and surface the operator's identity in the UI.
 *
 * Returns `null` on any malformed input rather than throwing, so callers can
 * branch cleanly without try/catch noise.
 *
 * Claim names are saas-auth's (the minter's) for a reseller session (H §1, §4.4):
 * `aud` (string or array; a reseller token carries `journix-reseller`),
 * `principal_type` (`RESELLER`), `resellerOf` (the numeric reseller id),
 * `resellerRole` (`OWNER` / `ADMIN` / `STAFF` — a hint up to 15 minutes stale; the
 * live role is tmm-api's `GET /v1/reseller-admin/me`), `productId` / `productSlug`,
 * plus the standard identity claims. A reseller token carries no tenant claims.
 */
export interface JwtPayload {
  sub?: string;
  aud?: string | string[];
  exp?: number;
  iat?: number;
  email?: string;
  name?: string;
  principal_type?: string;
  resellerOf?: number | string;
  resellerRole?: string;
  productId?: number | string;
  productSlug?: string;
  [key: string]: unknown;
}

export function decodeJwtPayload(token: string | null | undefined): JwtPayload | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payloadB64 = parts[1];
  if (!payloadB64) return null;
  try {
    const base64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json =
      typeof atob === 'function' ? atob(padded) : Buffer.from(padded, 'base64').toString('utf-8');
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/** The `aud` claim as a list — saas-auth may mint it as a string or an array. */
export function audiencesOf(claims: JwtPayload | null): string[] {
  const aud = claims?.aud;
  if (typeof aud === 'string') return [aud];
  if (Array.isArray(aud)) return aud.filter((a): a is string => typeof a === 'string');
  return [];
}
